/**
 * Field System Service
 * Unified service for managing both custom fields and list configurations
 * Integrates with backend field-system module API
 */

import apiService from './apiClient';
import logger from '../utils/logger';
import { DynamicFieldType, DynamicFieldConfig } from './dynamicFields.service';
import { DynamicColumnType } from './dynamicColumns.service';

// Field System Types
export interface FieldDefinition {
  id: string;
  moduleId: number;
  formId: string;
  fieldKey: string;
  fieldLabel: string;
  fieldType: DynamicFieldType;
  fieldConfig?: DynamicFieldConfig;
  isRequired: boolean;
  isCustom: boolean;
  isActive: boolean;
  displayOrder: number;
  validation?: {
    pattern?: string;
    minLength?: number;
    maxLength?: number;
    min?: number;
    max?: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface FormConfiguration {
  id: string;
  moduleId: number;
  formId: string;
  formName: string;
  fields: FieldDefinition[];
  defaultFields?: Array<{
    fieldName: string;
    fieldLabel: string;
    fieldType: string;
    fieldSource: string;
    sequence: number;
    validationRules?: Record<string, any>;
  }>;
  customFields?: Array<{
    id: string;
    fieldName: string;
    fieldLabel: string;
    fieldType: string;
    fieldSource: string;
    fieldDescription?: string;
    fieldOptions?: Array<{ value: string; label: string }>;
    validationRules?: Record<string, any>;
    placeholder?: string;
    helpText?: string;
    isActive: boolean;
    sequence: number;
  }>;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ListConfiguration {
  id?: string;
  moduleId?: number;
  listId: string;
  listName?: string;
  columns?: {
    fieldKey: string;
    label: string;
    type: DynamicColumnType;
    width?: number;
    sortable: boolean;
    visible: boolean;
    order: number;
    isCustom: boolean;
  }[];
  defaultColumns?: {
    id: string;
    fieldName: string;
    label: string;
    visible: boolean;
    width?: number;
    sortable: boolean;
    order: number;
    isCustom: boolean;
  }[];
  customColumns?: {
    id: string;
    fieldName: string;
    label: string;
    type: string;
    visible: boolean;
    sortable: boolean;
    width?: number;
    isCustom: boolean;
  }[];
  configuration?: any;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// DTOs
export interface UpdateListColumnDto {
  columns: Array<{
    fieldKey: string;
    label: string;
    type: DynamicColumnType;
    width?: number;
    sortable?: boolean;
    visible?: boolean;
    order: number;
  }>;
}

export interface FieldSystemModule {
  id: string;
  name: string;
  label: string;
  description: string;
  hasCustomFields: boolean;
  hasListConfig: boolean;
  forms?: Array<{
    id: string;
    name: string;
    label: string;
  }>;
  lists?: Array<{
    id: string;
    name: string;
    label: string;
  }>;
}

class FieldSystemService {
  private baseUrl = '/web/field-system';

  /**
   * Module ID to String Mapping
   * Only includes the 9 modules that support custom fields
   * Maps to backend FieldSystemModuleId enum values
   */
  private moduleIdToStringMap: Record<number, string> = {
    102: 'primary_customer',
    104: 'product',
    108: 'lead',
    109: 'secondary_customer',
    115: 'sites',
    116: 'direct_network',
    120: 'influencer_customer',
    139: 'user',
  };

  /**
   * Convert module ID to string identifier
   * Handles both numeric and string module IDs
   */
  private getModuleIdentifier(moduleId: number | string): string {
    if (typeof moduleId === 'string') {
      return moduleId;
    }

    const mappedId = this.moduleIdToStringMap[moduleId];
    if (!mappedId) {
      logger.warn(`No mapping found for module ID ${moduleId}, using numeric value`);
      return String(moduleId);
    }

    return mappedId;
  }

  /**
   * Get form configuration (all fields including system and custom)
   * GET /web/field-system/modules/:moduleId/forms/:formId/config
   */
  async getFormConfiguration(moduleId: number, formId: string): Promise<FormConfiguration> {
    try {
      const moduleIdentifier = this.getModuleIdentifier(moduleId);
      logger.info('Fetching form configuration', { moduleId, moduleIdentifier, formId });

      const response = await apiService.get(
        `${this.baseUrl}/modules/${moduleIdentifier}/forms/${formId}/config`
      );

      return response.data || response;
    } catch (error) {
      logger.error('Failed to fetch form configuration', error);
      throw error;
    }
  }

  /**
   * Get all fields (default + custom) for a form
   * GET /web/field-system/modules/:moduleId/forms/:formId/fields
   */
  async getAllFields(moduleId: number, formId: string): Promise<any[]> {
    try {
      const moduleIdentifier = this.getModuleIdentifier(moduleId);
      logger.info('Fetching all fields', { moduleId, moduleIdentifier, formId });

      const response = await apiService.get(
        `${this.baseUrl}/modules/${moduleIdentifier}/forms/${formId}/fields`
      );

      return response.data || response || [];
    } catch (error) {
      logger.error('Failed to fetch all fields', error);
      throw error;
    }
  }

  /**
   * Get list configuration (all columns including system and custom)
   * GET /web/field-system/modules/:moduleId/lists/:listId/config
   */
  async getListConfiguration(moduleId: number, listId: string): Promise<ListConfiguration> {
    try {
      const moduleIdentifier = this.getModuleIdentifier(moduleId);
      logger.info('Fetching list configuration', { moduleId, moduleIdentifier, listId });

      const response = await apiService.get(
        `${this.baseUrl}/modules/${moduleIdentifier}/lists/${listId}/config`
      );

      return response.data || response;
    } catch (error) {
      logger.error('Failed to fetch list configuration', error);
      throw error;
    }
  }

  /**
   * Save list configuration
   * POST /web/field-system/modules/:moduleId/lists/:listId/config
   */
  async saveListConfiguration(
    moduleId: number,
    listData: {
      listId: string;
      listName: string;
      columns: UpdateListColumnDto['columns'];
    }
  ): Promise<ListConfiguration> {
    try {
      const moduleIdentifier = this.getModuleIdentifier(moduleId);
      logger.info('Saving list configuration', { moduleId, moduleIdentifier, listData });

      const response = await apiService.post(
        `${this.baseUrl}/modules/${moduleIdentifier}/lists/${listData.listId}/config`,
        listData
      );

      return response.data || response;
    } catch (error) {
      logger.error('Failed to save list configuration', error);
      throw error;
    }
  }

  /**
   * Update list configuration
   * POST /web/field-system/modules/:moduleId/lists/:listId/config
   */
  async updateListConfiguration(
    listId: string,
    updateData: UpdateListColumnDto
  ): Promise<ListConfiguration> {
    try {
      logger.info('Updating list configuration', { listId, updateData });

      // Extract moduleId from listId (e.g., sales_user_list -> user)
      const moduleId = this.extractModuleIdFromListId(listId);

      const response = await apiService.post(
        `${this.baseUrl}/modules/${moduleId}/lists/${listId}/config`,
        updateData
      );

      return response.data || response;
    } catch (error) {
      logger.error('Failed to update list configuration', error);
      throw error;
    }
  }

  /**
   * Extract moduleId from listId
   * e.g., sales_user_list -> user, product_list -> product_master
   * Only maps to the 9 modules that support custom fields
   */
  private extractModuleIdFromListId(listId: string): string {
    const id = listId.toLowerCase();

    if (id.includes('user')) return 'user';
    if (id.includes('product')) return 'product_master';
    if (id.includes('primary_customer')) return 'primary_customer';
    if (id.includes('secondary_customer')) return 'secondary_customer';
    if (id.includes('influencer')) return 'influencer_customer';
    if (id.includes('direct_network') || id.includes('tertiary')) return 'direct_network';
    if (id.includes('customer') || id.includes('network')) return 'primary_customer';
    if (id.includes('site')) return 'sites';
    if (id.includes('lead')) return 'lead';

    logger.warn(`Unmapped listId: ${listId}, defaulting to 'user'`);
    return 'user';
  }

  /**
   * Add custom field to a form
   * POST /web/field-system/modules/:moduleId/forms/:formId/fields
   */
  async addCustomField(
    moduleId: number,
    formId: string,
    fieldData: {
      fieldName: string;
      fieldLabel: string;
      fieldType: string;
      fieldDescription?: string;
      placeholder?: string;
      helpText?: string;
      isRequired?: boolean;
      fieldOptions?: Array<{ value: string; label: string }>;
      validationRules?: Record<string, any>;
      subModuleType?: string; // For user module: 'sales_user' or 'backend_user'
    }
  ): Promise<any> {
    try {
      const moduleIdentifier = this.getModuleIdentifier(moduleId);
      logger.info('Adding custom field', { moduleId, moduleIdentifier, formId, fieldData });

      const response = await apiService.post(
        `${this.baseUrl}/modules/${moduleIdentifier}/forms/${formId}/fields`,
        fieldData
      );

      return response.data || response;
    } catch (error) {
      logger.error('Failed to add custom field', error);
      throw error;
    }
  }

  /**
   * Update custom field
   * PUT /web/field-system/modules/:moduleId/forms/:formId/fields/:fieldId
   */
  async updateCustomField(
    moduleId: number,
    formId: string,
    fieldId: string,
    fieldData: {
      fieldLabel?: string;
      fieldType?: string;
      fieldDescription?: string;
      placeholder?: string;
      helpText?: string;
      isRequired?: boolean;
      fieldOptions?: Array<{ value: string; label: string }>;
      validationRules?: Record<string, any>;
      subModuleType?: string | null; // For user module: 'sales_user' or 'backend_user'
    }
  ): Promise<any> {
    try {
      const moduleIdentifier = this.getModuleIdentifier(moduleId);
      logger.info('Updating custom field', { moduleId, moduleIdentifier, formId, fieldId, fieldData });

      const response = await apiService.put(
        `${this.baseUrl}/modules/${moduleIdentifier}/forms/${formId}/fields/${fieldId}`,
        fieldData
      );

      return response.data || response;
    } catch (error) {
      logger.error('Failed to update custom field', error);
      throw error;
    }
  }

  /**
   * Delete custom field
   * DELETE /web/field-system/modules/:moduleId/forms/:formId/fields/:fieldId
   */
  async deleteCustomField(
    moduleId: number,
    formId: string,
    fieldId: string
  ): Promise<void> {
    try {
      const moduleIdentifier = this.getModuleIdentifier(moduleId);
      logger.info('Deleting custom field', { moduleId, moduleIdentifier, formId, fieldId });

      await apiService.delete(
        `${this.baseUrl}/modules/${moduleIdentifier}/forms/${formId}/fields/${fieldId}`
      );
    } catch (error) {
      logger.error('Failed to delete custom field', error);
      throw error;
    }
  }

  /**
   * Get field values for a record
   * GET /web/field-system/modules/:moduleId/entities/:entityId/field-values
   */
  async getFieldValues(
    moduleId: number,
    formId: string,
    recordId: string
  ): Promise<Record<string, any>> {
    try {
      const moduleIdentifier = this.getModuleIdentifier(moduleId);
      logger.info('Fetching field values', { moduleId, moduleIdentifier, formId, recordId });

      const response = await apiService.get(
        `${this.baseUrl}/modules/${moduleIdentifier}/entities/${recordId}/field-values`
      );

      return response.data || response || {};
    } catch (error) {
      logger.warn('Failed to fetch field values', error);
      return {};
    }
  }

  /**
   * Save field values for a record
   * POST /web/field-system/modules/:moduleId/entities/:entityId/field-values
   */
  async saveFieldValues(
    moduleId: number,
    formId: string,
    recordId: string,
    values: Record<string, any>
  ): Promise<void> {
    try {
      const moduleIdentifier = this.getModuleIdentifier(moduleId);
      logger.info('Saving field values', { moduleId, moduleIdentifier, formId, recordId });

      await apiService.post(
        `${this.baseUrl}/modules/${moduleIdentifier}/entities/${recordId}/field-values`,
        values
      );
    } catch (error) {
      logger.error('Failed to save field values', error);
      throw error;
    }
  }

  /**
   * Get module statistics
   * GET /web/field-system/modules/:moduleId/statistics
   */
  async getModuleStatistics(moduleId: number): Promise<any> {
    try {
      const moduleIdentifier = this.getModuleIdentifier(moduleId);
      logger.info('Fetching module statistics', { moduleId, moduleIdentifier });

      const response = await apiService.get(
        `${this.baseUrl}/modules/${moduleIdentifier}/statistics`
      );

      return response.data || response;
    } catch (error) {
      logger.error('Failed to fetch module statistics', error);
      throw error;
    }
  }

}

export const fieldSystemService = new FieldSystemService();
export default fieldSystemService;